import gql from 'graphql-tag';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import * as MoleculeActions from 'chemistry/molecules/MoleculeActions';
import {updateMoleculeVersion} from 'chemistry/molecules/MoleculeVersionReduxActions';
import {Molecule, MoleculeHistoryPanelState} from 'chemistry/molecules/tabs/MoleculeFileState';
import DataConnector from 'connect/DataConnector';
import {CHEMISTRY_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import CloneModal from 'folder-items/CloneModal';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getStore} from 'redux-stores/Store';
import {
  MoleculeVersionQuery,
  MoleculeVersionQueryVariables,
} from 'toolbar/molecule/__generated/MoleculeVersionQuery';
import ReactPanelBaseView, {getTabAndPanelIds, Subpanel} from 'toolbar/ReactPanelBaseView';
import HistoryPanelComponent, {Version} from 'toolbar/shared/HistoryPanelComponent';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import legacyRunModal from 'ui-library/legacyRunModal';

type MoleculeVersionQueryMoleculeVersion = ExtractByTypename<
  MoleculeVersionQuery['molecule'],
  'MoleculeNode'
>['versions'][number];

const MOLECULE_VERSION_QUERY = gql`
  query MoleculeVersionQuery($moleculeId: StrictString!) @skipAudit @engComponent(name: CHEMISTRY) {
    molecule(id: $moleculeId) {
      ... on MoleculeNode {
        id
        versions {
          id
          name
          creator {
            id
            name
          }
          modifiedAt
          parentId
        }
        currentVersion {
          id
          name
          creator {
            id
            name
          }
          modifiedAt
          parentId
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface ConnectedMoleculeHistoryPanelProps {
  openMoleculeVersionId: string;
}

export interface UnconnectedMoleculeHistoryPanelProps {
  id: string;
  name: string;
  folderId: string;
  permissions?: Permissions;
}

export type MoleculeHistoryPanelProps = ConnectedMoleculeHistoryPanelProps &
  UnconnectedMoleculeHistoryPanelProps;

class MoleculeHistoryPanelConnector extends DataConnector<
  UnconnectedMoleculeHistoryPanelProps,
  ConnectedMoleculeHistoryPanelProps
> {
  enhanceProps(): ConnectedMoleculeHistoryPanelProps {
    const openMoleculeVersionId =
      this.reduxState().ui.chemistry.moleculeVersionUIs[this.props.id].moleculeVersionId;
    return {openMoleculeVersionId};
  }
}

class MoleculeHistoryPanel extends React.Component<MoleculeHistoryPanelProps> {
  openVersion = (versionId: string): void => {
    getStore().dispatch(updateMoleculeVersion(this.props.id, versionId));
    analytics.trackEvent(CHEMISTRY_ANALYTICS_ACTIONS.VIEWED_MOLECULE, {
      moleculeId: this.props.id,
      moleculeVersionId: versionId,
    });
  };

  resetToVersion = (): void => {
    MoleculeActions.resetToVersion(this.props.id, this.props.openMoleculeVersionId);
  };

  cloneFromVersion = (): void => {
    legacyRunModal(CloneModal, {
      modelType: ModelTypeConstants.MOLECULE,
      id: this.props.id,
      versionId: this.props.openMoleculeVersionId,
      initialName: `${this.props.name} (new)`,
      initialFolderId: this.props.folderId,
    });
  };

  formatVersions = (versions: Array<MoleculeVersionQueryMoleculeVersion>): Array<Version> => {
    const formattedVersions: Array<Version> = [];
    for (const version of versions) {
      formattedVersions.push({...version, fetching: false});
    }
    return formattedVersions;
  };

  override render(): JSX.Element {
    return (
      <BenchlingQuery<MoleculeVersionQuery, MoleculeVersionQueryVariables>
        query={MOLECULE_VERSION_QUERY}
        renderLoading={() => <CenteredHorizontalThrobber />}
        variables={{moleculeId: this.props.id}}
      >
        {(data, refetch) => {
          assert(!isInaccessible(data.molecule), 'Molecule must be accessible.');
          return (
            <HistoryPanelComponent
              openVersion={this.openVersion}
              resetToVersion={this.resetToVersion}
              cloneFromVersion={this.cloneFromVersion}
              versions={this.formatVersions(data.molecule.versions)}
              openVersionId={this.props.openMoleculeVersionId}
              isLatest={data.molecule.currentVersion.id === this.props.openMoleculeVersionId}
              unsaved={false}
              showNames={true}
              showSeconds={true}
              hasParent={false}
              parent={null}
              permissions={this.props.permissions}
              disableHistoryChanges={!BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE}
            />
          );
        }}
      </BenchlingQuery>
    );
  }
}

const ConnectedMoleculeHistoryPanel = MoleculeHistoryPanelConnector.connect(MoleculeHistoryPanel);

class MoleculeHistoryPanelView extends ReactPanelBaseView<MoleculeHistoryPanelState, Molecule> {
  getSubpanels(): Array<Subpanel> {
    return [
      {
        ...getTabAndPanelIds('history'),
        title: 'History',
        component: (
          <ConnectedMoleculeHistoryPanel
            id={this.model.id}
            name={this.model.name()}
            folderId={this.model.folder().id}
            permissions={this.model.permissions()}
          />
        ),
      },
    ];
  }
}

export default MoleculeHistoryPanelView;
